/**
 * Main layout component with role-based navigation sidebar.
 * Filters navigation items based on user role.
 */

import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useAuth, UserRole } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'

interface NavigationItem {
  name: string
  href: string
  roles?: UserRole[] // If undefined, accessible to all authenticated users
}

// Navigation items with role-based access
const allNavigation: NavigationItem[] = [
  { name: 'Dashboard', href: '/' },
  { name: 'Applications', href: '/applications', roles: ['admin'] },
  { name: 'Products', href: '/products' },
  { name: 'Orders', href: '/orders' },
  { name: 'Payments', href: '/payments' },
  { name: 'Request Logs', href: '/request-logs', roles: ['admin'] },
  // Read-only Accounts views are offered to admin and app_owner. The hidden
  // entry is not access control: the route itself carries no role wrapper.
  { name: 'Accounts', href: '/accounts', roles: ['admin', 'app_owner'] },
  { name: 'Subscriptions', href: '/subscriptions' },
  { name: 'Payment Config', href: '/payment-configs', roles: ['admin'] },
]

export default function Layout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout, hasRole } = useAuth()

  // Filter navigation based on user role
  const navigation = allNavigation.filter((item) => {
    if (!item.roles) return true // No role restriction
    return item.roles.some((role) => hasRole(role))
  })

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <div className="fixed inset-y-0 z-50 flex w-64 flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-border bg-card px-6 pb-4">
          <div className="flex h-16 shrink-0 items-center">
            <span className="text-xl font-bold">Commerce System</span>
          </div>
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  {navigation.map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={cn(
                          location.pathname === item.href
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                          'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                        )}
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
              {/* User section at bottom */}
              <li className="mt-auto">
                <div className="flex flex-col gap-2 -mx-2 p-2 rounded-md bg-muted/50">
                  <div className="text-sm font-medium truncate">{user?.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                  <div className="text-xs text-muted-foreground capitalize">Role: {user?.role}</div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLogout}
                    className="mt-2 w-full"
                  >
                    Sign Out
                  </Button>
                </div>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="pl-64">
        <main className="py-10">
          <div className="px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
